import { useCallback, useEffect, useState } from 'react';
import { getAdminSession } from '../adminSession';
import {
  fetchPayrollRecordDetail,
  getPayrollSettingsErrorMessage,
  type PayrollRecord,
} from './payrollSettingsApi';
import { numberKRW, formatHours } from './payrollFormat';
import './PayrollRecords.scss';

type Props = {
  id: number;
  onBack: () => void;
};

export default function PayrollRecordDetail({ id, onBack }: Props) {
  const session = getAdminSession();

  const [record, setRecord] = useState<PayrollRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRecord = useCallback(async () => {
    if (!session?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPayrollRecordDetail(session.id, id);
      setRecord(data);
    } catch (err) {
      setError(getPayrollSettingsErrorMessage(err, '급여명세서를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [session?.id, id]);

  useEffect(() => {
    void loadRecord();
  }, [loadRecord]);

  return (
    <div className="payroll-records-detail">
      <div className="payroll-records-list__header">
        <button type="button" className="payroll-records-list__back-btn" onClick={onBack}>
          ← 목록으로
        </button>
        <h3 className="payroll-records-list__title">급여명세서 상세</h3>
      </div>

      {loading && <p className="payroll-records-detail__loading">불러오는 중…</p>}
      {error && <p className="payroll-records-list__error">{error}</p>}

      {record && (
        <div className="payroll-records-detail__card">
          <div className="payroll-records-detail__info">
            <div className="payroll-records-detail__info-row">
              <span>성명</span>
              <strong>{record.employeeName}</strong>
            </div>
            <div className="payroll-records-detail__info-row">
              <span>소속</span>
              <strong>{[record.department, record.position].filter(Boolean).join(' · ') || '-'}</strong>
            </div>
            <div className="payroll-records-detail__info-row">
              <span>지급 대상기간</span>
              <strong>{record.year}년 {record.month}월</strong>
            </div>
            <div className="payroll-records-detail__info-row">
              <span>급여 형태</span>
              <strong>{record.payType === 'salary' ? '정직원급여' : '시급급여'}</strong>
            </div>
            {record.payType === 'hourly' && (
              <>
                <div className="payroll-records-detail__info-row">
                  <span>시급</span>
                  <strong>{numberKRW(record.hourlyWage)}</strong>
                </div>
                <div className="payroll-records-detail__info-row">
                  <span>순 근무시간</span>
                  <strong>{formatHours(record.monthlyWorkHours)}</strong>
                </div>
                <div className="payroll-records-detail__info-row">
                  <span>주휴시간</span>
                  <strong>{formatHours(record.monthlyAllowanceHours)}</strong>
                </div>
                <div className="payroll-records-detail__info-row">
                  <span>총 근무시간</span>
                  <strong>{formatHours(record.monthlyTotalHours)}</strong>
                </div>
              </>
            )}
          </div>

          <div className="payroll-records-detail__table">
            <div className="payroll-records-detail__table-head">
              <span>지급 항목</span>
              <span>금액</span>
            </div>
            {record.payType === 'hourly' ? (
              <>
                <div className="payroll-records-detail__table-row">
                  <span>기본급</span>
                  <span>{numberKRW(record.basePay)}</span>
                </div>
                <div className="payroll-records-detail__table-row">
                  <span>주휴수당</span>
                  <span>{numberKRW(record.allowancePay)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="payroll-records-detail__table-row">
                  <span>월 급여</span>
                  <span>{numberKRW(record.basePay)}</span>
                </div>
                {record.overtimePay > 0 && (
                  <div className="payroll-records-detail__table-row">
                    <span>
                      추가근무수당 (
                      {[
                        record.overtimeDays > 0 ? `${record.overtimeDays}일` : '',
                        record.overtimeHours > 0 ? `${record.overtimeHours}시간` : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      )
                    </span>
                    <span>{numberKRW(record.overtimePay)}</span>
                  </div>
                )}
              </>
            )}
            <div className="payroll-records-detail__table-row is-total">
              <span>지급 합계</span>
              <span>{numberKRW(record.grossPay)}</span>
            </div>
          </div>

          <div className="payroll-records-detail__table">
            <div className="payroll-records-detail__table-head">
              <span>공제 항목</span>
              <span>금액</span>
            </div>
            {record.deductionMode === 'none' && (
              <div className="payroll-records-detail__table-row payroll-records-detail__table-row--empty">
                <span>공제 항목 없음</span>
              </div>
            )}
            {record.deductionMode === 'insurance' && (
              <>
                <div className="payroll-records-detail__table-row">
                  <span>국민연금 ({record.pensionRate}%)</span>
                  <span>-{numberKRW(record.pensionAmount)}</span>
                </div>
                <div className="payroll-records-detail__table-row">
                  <span>건강보험 ({record.healthRate}%)</span>
                  <span>-{numberKRW(record.healthAmount)}</span>
                </div>
                <div className="payroll-records-detail__table-row">
                  <span>장기요양보험 ({record.longTermCareRate}%)</span>
                  <span>-{numberKRW(record.longTermCareAmount)}</span>
                </div>
                <div className="payroll-records-detail__table-row">
                  <span>고용보험 ({record.employmentRate}%)</span>
                  <span>-{numberKRW(record.employmentAmount)}</span>
                </div>
                <div className="payroll-records-detail__table-row">
                  <span>소득세 + 지방소득세</span>
                  <span>-{numberKRW(record.incomeTax)}</span>
                </div>
                {record.etcDeduction > 0 && (
                  <div className="payroll-records-detail__table-row">
                    <span>기타 공제</span>
                    <span>-{numberKRW(record.etcDeduction)}</span>
                  </div>
                )}
              </>
            )}
            {record.deductionMode === 'freelance' && (
              <>
                <div className="payroll-records-detail__table-row">
                  <span>사업소득세 ({record.freelanceRate}%)</span>
                  <span>-{numberKRW(record.freelanceAmount)}</span>
                </div>
                {record.etcDeduction > 0 && (
                  <div className="payroll-records-detail__table-row">
                    <span>기타 공제</span>
                    <span>-{numberKRW(record.etcDeduction)}</span>
                  </div>
                )}
              </>
            )}
            {record.deductionMode !== 'none' && (
              <div className="payroll-records-detail__table-row is-total">
                <span>공제 합계</span>
                <span>-{numberKRW(record.totalDeductions)}</span>
              </div>
            )}
          </div>

          <div className="payroll-records-detail__net">
            <span>실수령액</span>
            <strong>{numberKRW(record.netPay)}</strong>
          </div>

          <p className="payroll-records-detail__meta">
            저장일시: {record.updatedAt ? new Date(record.updatedAt).toLocaleString('ko-KR') : '-'}
          </p>
        </div>
      )}
    </div>
  );
}
